import { logger } from '../../utils/logger';

/**
 * MSAL Error Handler - Handles and recovers from MSAL-specific errors
 */

export interface MsalErrorData {
  failedRequests?: [number, string][];
  errors?: string[];
  cacheHits?: number;
}

const CLEANUP_INTERVAL_MS = 30 * 60 * 1000;
const MSAL_REQUEST_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

let cleanupIntervalId: ReturnType<typeof setInterval> | null = null;

/**
 * Check for MSAL errors in localStorage
 */
const checkMsalErrors = (): MsalErrorData | null => {
  try {
    // Check for MSAL error entries in localStorage
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.includes('msal') && key.includes('error')) {
        const errorData = localStorage.getItem(key);
        if (errorData) {
          try {
            const parsed = JSON.parse(errorData);
            if (parsed.errors && parsed.errors.length > 0) {
              logger.warn(
                'Found MSAL errors in cache: ' + JSON.stringify(parsed),
              );
              return parsed;
            }
          } catch (e) {
            // Not JSON, ignore
          }
        }
      }
    }
  } catch (error) {
    logger.error(
      'Error checking MSAL errors: ' +
        (error instanceof Error ? error.message : String(error)),
    );
  }
  return null;
};

/**
 * Clear MSAL error entries from localStorage
 */
export const clearMsalErrors = (): void => {
  try {
    const keys = Object.keys(localStorage);
    let clearedCount = 0;

    for (const key of keys) {
      if (
        key.includes('msal') &&
        (key.includes('error') || key.includes('failed'))
      ) {
        logger.debug(`Clearing MSAL error entry: ${key}`);
        localStorage.removeItem(key);
        clearedCount++;
      }
    }

    if (clearedCount > 0) {
      logger.info(`Cleared ${clearedCount} MSAL error entries from cache`);
    }
  } catch (error) {
    logger.error(
      'Error clearing MSAL errors: ' +
        (error instanceof Error ? error.message : String(error)),
    );
  }
};

/**
 * Clear stale MSAL cache entries that might cause issues
 */
export const clearStaleMsalCache = (): void => {
  try {
    const keys = Object.keys(localStorage);
    const now = Date.now();
    let clearedCount = 0;

    for (const key of keys) {
      if (key.includes('msal') && key.includes('idtoken')) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const parsed = JSON.parse(value);
            // Check if token is expired (add 24 hours grace period)
            if (parsed.expiresOn) {
              const expiresOn = parseInt(parsed.expiresOn) * 1000; // Convert to ms
              if (now > expiresOn + 24 * 60 * 60 * 1000) {
                logger.debug(`Clearing stale MSAL token: ${key}`);
                localStorage.removeItem(key);
                clearedCount++;
              }
            }
          } catch (e) {
            // Not JSON or invalid format, consider it stale
            logger.debug(`Clearing invalid MSAL entry: ${key}`);
            localStorage.removeItem(key);
            clearedCount++;
          }
        }
      }

      // Also clear failed request tracking
      if (key.includes('msal') && key.includes('request')) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const parsed = JSON.parse(value);
            const timestampSeconds =
              typeof parsed.timestamp === 'number'
                ? parsed.timestamp
                : typeof parsed.timestamp === 'string'
                  ? parseInt(parsed.timestamp, 10)
                  : NaN;

            if (Number.isNaN(timestampSeconds)) {
              logger.debug(`Skipping MSAL request without timestamp: ${key}`);
              continue;
            }

            const ageMs = now - timestampSeconds * 1000;
            if (ageMs > MSAL_REQUEST_MAX_AGE_MS) {
              logger.debug(`Clearing stale MSAL request: ${key}`);
              localStorage.removeItem(key);
              clearedCount++;
            }
          } catch (e) {
            // Clear invalid entries
            logger.debug(`Clearing invalid MSAL request entry: ${key}`);
            localStorage.removeItem(key);
            clearedCount++;
          }
        }
      }
    }

    if (clearedCount > 0) {
      logger.info(`Cleared ${clearedCount} stale MSAL cache entries`);
    }
  } catch (error) {
    logger.error(
      'Error clearing stale MSAL cache: ' +
        (error instanceof Error ? error.message : String(error)),
    );
  }
};

/**
 * Handle specific MSAL error types
 */
export const handleMsalError = (error: any): boolean => {
  if (!error) return false;

  const errorCode = error.errorCode || error.code || '';
  const errorMessage = error.errorMessage || error.message || '';

  logger.error(
    'MSAL Error: ' +
      'errorCode=' +
      errorCode +
      ', errorMessage=' +
      errorMessage +
      ', error=' +
      (error instanceof Error ? error.message : JSON.stringify(error)),
  );

  // Handle specific error types
  switch (errorCode.toLowerCase()) {
    case 'post_request_failed':
      logger.warn('Post request failed - clearing cache and retrying');
      clearMsalErrors();
      clearStaleMsalCache();
      return true;

    case 'interaction_in_progress':
      logger.warn('Interaction already in progress');
      return false;

    case 'block_token_requests':
      logger.warn('Token requests are blocked - clearing cache');
      clearMsalErrors();
      return true;

    case 'network_error':
      logger.warn('Network error during MSAL operation');
      return false;

    default:
      // For unknown errors, log and clear errors
      if (errorMessage.includes('failed') || errorMessage.includes('error')) {
        clearMsalErrors();
        return true;
      }
      return false;
  }
};

/**
 * Initialize MSAL error handling
 */
export const initializeMsalErrorHandling = (): void => {
  // Check for existing errors on startup
  const errors = checkMsalErrors();
  if (
    errors &&
    errors.errors &&
    errors.errors.includes('post_request_failed')
  ) {
    logger.warn(
      'Found post_request_failed error from previous session, cleaning up...',
    );
    clearMsalErrors();
    clearStaleMsalCache();
  }

  // Set up periodic cleanup (every 30 minutes)
  if (cleanupIntervalId === null) {
    cleanupIntervalId = setInterval(() => {
      clearStaleMsalCache();
    }, CLEANUP_INTERVAL_MS);
  }
};
